import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { useLocation } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';
import './DataSources.css';
import { apiService } from '../../../services/api';
import { getDatasetBadge, getDatasetCountLine } from '../../../utils/datasetBadge';
import { jobStatusToLabel } from '../../../utils/datasetStatusLabel';
import { isDatasetActive } from '../../../utils/datasetReviewState';
import { reconcileSelectedDataset } from '../../../utils/datasetSelection';
import { useCapabilities, useAllowedTabs } from '../../../utils/capabilities';
import { CsvReviewModel } from '../../../models/CsvReviewModel';
import { DocumentReviewModel } from '../../../models/DocumentReviewModel';
import { useDocumentReviewViewModel } from '../../../viewModels/DocumentReviewViewModel';
import DocumentReviewPanel from '../DocumentReview/DocumentReviewPanel';
import DatasetDetails from './DatasetDetails';
import DatasetCardTime from './DatasetCardTime';
import DatasetMenuItems, { canShowDatasetMenu } from './DatasetMenuItems';
import AddConnectionModal from './AddConnectionModal';
import DeleteDatasetModal from './DeleteDatasetModal';
import EditDatasetModal from './EditDatasetModal';
import { useConfirm } from '../shared/useConfirm';
import { shareImpactMessage } from '../../../utils/sharingImpact';
import Pagination from '../shared/Pagination';
import RowActionsMenu from '../shared/RowActionsMenu';
import Assessments from '../Assessments/Assessments';

/**
 * View Component for Data Sources Page
 */
const DataSources = ({
  onNavigateToCreate,
  onShowNotification,
  enableCsvHitlReview,
  submittedReviewJobs,
  onOpenReview,
  datasetsRefreshKey = 0,
  features = {},
  showAiIndexedSummary = false,
  wizardIsOpen = false,
  assessmentsModel = null,
  onShowToast,
  onManageScenarios,
  onAddAssessmentHandled,
}) => {
  // PRD-962: render the server-computed status badge + count line instead of the
  // legacy job_status badge. Old behaviour stays as the fallback when off.
  const statusBadgesEnabled = useFlag('dataset_status_badges');
  const [confirm, confirmDialog] = useConfirm();
  // PRD-979: gates the fire-and-navigate publish flow (draft rows + "Uploading files…"
  // rendering + polling for draft-with-no-job). Flag OFF = pre-PRD-979 behaviour.
  const autoPublishEnabled = useFlag('dataset_auto_publish');
  // Document field review (HITL). One shared Unleash flag with the backend, so a
  // single flip turns on both sides.
  //
  // NOTE — deliberate deviation from "gate every new feature with useFlag()":
  // the Review entry point and panel are gated on `file_counts.awaiting_review`
  // instead. The flag controls whether the backend PARKS documents, so with it
  // off the count is always 0 and the UI stays hidden anyway. But if the flag is
  // switched off while documents are already parked, they remain parked until a
  // human reviews them — a flag-gated button would hide the only drain path and
  // strand them invisibly, which is the same shape as the CSV latch bug. The
  // count is the correct gate; the flag only suppresses the polling
  // notification before rollout.
  const documentHitlReviewEnabled = useFlag('hitl-field-review');
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'published', 'processing', 'review', 'failed'
  const location = useLocation();
  // Org-dataset-sharing view: 'all' (mine + org-shared), 'mine', or 'shared'.
  // Ignored server-side when the org-dataset-sharing flag is off (returns 'mine').
  const [scopeFilter, setScopeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [previousJobStatuses, setPreviousJobStatuses] = useState({}); // Track previous job_status values
  const previousJobStatusesRef = useRef({});
  // Document field review reports through file_counts.awaiting_review, not
  // job_status, so it needs its own previous-value tracker to spot the 0 -> N
  // transition that tells a reviewer there is work waiting.
  const previousAwaitingReviewRef = useRef({});

  // Document field review panel. The model is created once and shared with the
  // ViewModel, per the MVVM pattern.
  const documentReviewModel = useMemo(() => new DocumentReviewModel(), []);
  const documentReviewVm = useDocumentReviewViewModel(documentReviewModel);
  // The dataset under review, not just its id: the back button needs a record to
  // reopen Dataset details with, and looking one up in the current page fails
  // the moment the reviewer has paginated or filtered. The opener always has the
  // full record, so keep it. Same snapshot pattern as `selectedDataset`, and
  // refreshed by the same effect below.
  const [documentReviewDataset, setDocumentReviewDataset] = useState(null);
  const datasetsEtagRef = useRef(null);
  // PRD-1699: with `role-capabilities` on, only the tabs the role allows show, and the
  // page opens on the first of them. With none of them (Assessments only, before its
  // tab exists) no tab shows and nothing is listed.
  const caps = useCapabilities(features);
  // PRD-1703: Assessments joins them while `navy-assessments` is on (App passes
  // the model then), beside Datasets and Connections; where the page lands is
  // left alone until Product confirms the handoff's landing on Assessments.
  const { shown: dataSourcesTabs, active: activeTab, setActive: setActiveTab } = useAllowedTabs(caps, [
    { id: 'datasets', label: 'Datasets', allowed: caps.has('datasets') },
    { id: 'connections', label: 'Connections', allowed: caps.has('connections') },
    ...(assessmentsModel ? [{ id: 'assessments', label: 'Assessments', allowed: caps.has('assessments') }] : []),
  ]);
  // The Add assessment button that opened the dialog, while it's open.
  const [assessmentOpener, setAssessmentOpener] = useState(null);
  const addAssessmentRef = useRef(null);
  const closeAddAssessment = useCallback(() => setAssessmentOpener(null), []);
  // Back from Settings > Assessments ("Return to add assessment"): open the
  // tab, then the dialog (its draft was kept) once the tab's Add assessment is
  // there to hand focus back to. The request is cleared once met, so a reload
  // or a revisit doesn't open the dialog again.
  const reopenAssessment = !!assessmentsModel && !!location.state?.addAssessment;
  const [pendingReopen, setPendingReopen] = useState(false);
  useEffect(() => {
    if (!reopenAssessment) return;
    setActiveTab('assessments');
    setPendingReopen(true);
    onAddAssessmentHandled?.();
  }, [reopenAssessment, setActiveTab, onAddAssessmentHandled]);
  useEffect(() => {
    if (!pendingReopen || activeTab !== 'assessments' || !addAssessmentRef.current) return;
    setAssessmentOpener(addAssessmentRef.current);
    setPendingReopen(false);
  }, [pendingReopen, activeTab]);
  // Without View Datasets the list is never asked for, whatever would refetch it.
  const datasetsAllowed = !caps.enabled || caps.has('datasets');
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [connections, setConnections] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingDataset, setEditingDataset] = useState(null);
  const [deletingDataset, setDeletingDataset] = useState(null);
  // Connection overflow menu — Edit reuses AddConnectionModal pre-filled;
  // Delete uses the browser's native confirm dialog.
  const [editingConnection, setEditingConnection] = useState(null);
  const pageSize = 20;

  const loadDatasets = useCallback(async (silent = false) => {
    if (!datasetsAllowed) return;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const params = {
        page: currentPage,
        page_size: pageSize,
        scope: scopeFilter,
      };

      // Add status filter: API only supports 'published' (and 'draft'); for processing/failed we filter client-side
      if (statusFilter === 'published') {
        params.status = 'published';
      }
      // When 'processing' or 'failed', we don't pass status and filter items below

      const response = await apiService.getDatasets(params, silent ? datasetsEtagRef.current : null);

      // 304 Not Modified on a silent poll: nothing changed, skip all state updates
      if (response.notModified) {
        return;
      }
      datasetsEtagRef.current = response._etag || null;

      let items = response.items || [];
      const totalFromApi = response.total || 0;

      if (statusFilter === 'published') {
        items = items.filter((d) => {
          const job = (d.job_status || '').toLowerCase();
          return job === 'complete' || job === 'completed';
        });
      } else if (statusFilter === 'processing') {
        items = items.filter((d) => {
          const job = (d.job_status || '').toLowerCase();
          // Include datasets with an active job AND datasets freshly created with no job yet
          // (displayed as "Processing" on the card — keep filter consistent).
          return job === 'processing' || job === 'queued' || (!d.job_status && d.status === 'published');
        });
      } else if (statusFilter === 'review') {
        items = items.filter((d) => {
          const job = (d.job_status || '').toLowerCase();
          // Two independent review sources. CSV raises job_status
          // 'awaiting_input'; document field review leaves job_status untouched
          // and reports through file_counts.awaiting_review. Keyed off the count
          // rather than the badge on purpose: badge precedence is
          // processing > failed > review, so a dataset with a parked file AND a
          // failure carries `action_needed` and would be filtered out here.
          return job === 'awaiting_input' || (d.file_counts?.awaiting_review ?? 0) > 0;
        });
      } else if (statusFilter === 'failed') {
        items = items.filter((d) => {
          const job = (d.job_status || '').toLowerCase();
          return job === 'failed' || job === 'error';
        });
      }

      const total = totalFromApi;
      const calculatedTotalPages = response.total_pages || Math.ceil(totalFromApi / pageSize) || 1;

      // Check for job status changes (from non-complete to complete) using ref for latest values when polling
      const prevStatuses = previousJobStatusesRef.current;
      if (Object.keys(prevStatuses).length > 0 && onShowNotification) {
        items.forEach((dataset) => {
          const previousStatus = prevStatuses[dataset.id];
          const currentStatus = dataset.job_status;
          if (previousStatus !== undefined &&
              previousStatus !== null &&
              previousStatus !== 'complete' &&
              previousStatus !== 'completed' &&
              (currentStatus === 'complete' || currentStatus === 'completed')) {
            onShowNotification(`${dataset.name} dataset extraction has been completed`, 'success');
          }
          if (previousStatus !== undefined &&
              previousStatus !== null &&
              previousStatus !== 'awaiting_input' &&
              currentStatus === 'awaiting_input') {
            onShowNotification(`${dataset.name} requires your review before processing can continue`, 'info');
          }
        });
      }

      // Same notification for document field review, driven off the count
      // instead of job_status (which stays unchanged for this flow). Only the
      // 0 -> N edge fires, so a reviewer working through a backlog isn't
      // re-notified on every poll.
      const prevAwaiting = previousAwaitingReviewRef.current;
      if (documentHitlReviewEnabled && Object.keys(prevAwaiting).length > 0 && onShowNotification) {
        items.forEach((dataset) => {
          const previousCount = prevAwaiting[dataset.id];
          // Effective count is 0 while the batch is still processing, so the 0 -> N edge
          // fires exactly once, at batch settle — not per file as each one parks, and not
          // never (a count that rose mid-batch would otherwise pre-arm "previous" and
          // swallow the settle edge).
          const stillProcessing = (dataset.file_counts?.processing ?? 0) > 0;
          const currentCount = stillProcessing ? 0 : (dataset.file_counts?.awaiting_review ?? 0);
          if (previousCount !== undefined && previousCount === 0 && currentCount > 0) {
            onShowNotification(`${dataset.name} requires your review before processing can continue`, 'info');
          }
        });
      }

      const newPreviousStatuses = {};
      const newPreviousAwaiting = {};
      items.forEach((dataset) => {
        newPreviousStatuses[dataset.id] = dataset.job_status;
        newPreviousAwaiting[dataset.id] =
          (dataset.file_counts?.processing ?? 0) > 0 ? 0 : (dataset.file_counts?.awaiting_review ?? 0);
      });
      previousJobStatusesRef.current = { ...previousJobStatusesRef.current, ...newPreviousStatuses };
      previousAwaitingReviewRef.current = { ...previousAwaitingReviewRef.current, ...newPreviousAwaiting };
      setPreviousJobStatuses(prev => ({ ...prev, ...newPreviousStatuses }));

      setDatasets(items);
      setTotalPages(calculatedTotalPages);
      setTotalItems(total);
    } catch (err) {
      console.error('Error loading datasets:', err);
      setError(err.message || 'Failed to load datasets');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [statusFilter, scopeFilter, currentPage, pageSize, documentHitlReviewEnabled, datasetsAllowed]);

  useEffect(() => {
    loadDatasets();
  }, [loadDatasets]);

  // Refresh when page becomes visible (e.g. returning from another tab)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadDatasets(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadDatasets]);

  // Refetch when the create/edit wizard closes — the wizard is a modal over this page,
  // so visibilitychange doesn't fire and neither does a route change.
  const prevWizardOpenRef = useRef(wizardIsOpen);
  useEffect(() => {
    if (prevWizardOpenRef.current && !wizardIsOpen) {
      loadDatasets();
    }
    prevWizardOpenRef.current = wizardIsOpen;
  }, [wizardIsOpen, loadDatasets]);

  // Poll while at least one dataset is processing, queued, awaiting input, or
  // holding files parked for document field review.
  useEffect(() => {
    if (activeTab !== 'datasets') return;
    const hasActiveJob = datasets.some(
      (d) => isDatasetActive(d, { autoPublishEnabled }),
    );
    if (!hasActiveJob) return;
    const pollInterval = 10000;
    const interval = setInterval(() => loadDatasets(true), pollInterval);
    return () => clearInterval(interval);
  }, [activeTab, datasets, loadDatasets, autoPublishEnabled]);

  // `selectedDataset` is the snapshot taken when the card was clicked. Polling
  // replaces `datasets` but would leave that snapshot frozen, so a dataset that
  // settles into review-required — or has one of its parked files reviewed —
  // while its modal is open keeps rendering the state it had on open. Re-derive
  // it from every refreshed list.
  //
  // If the dataset is no longer in the list (a filter or page change dropped
  // it), the snapshot is kept rather than cleared: closing the modal out from
  // under the reviewer is worse than briefly showing stale counts.
  // The decisions live in reconcileSelectedDataset so they can be tested; see
  // utils/datasetSelection.test.js.
  useEffect(() => {
    setSelectedDataset((current) => reconcileSelectedDataset(current, datasets));
    // Same treatment for the dataset under review: it is the record the back
    // button will reopen Dataset details with, so it should be as fresh as the
    // list allows — and, by the same argument as above, is kept rather than
    // cleared when a filter or page change drops it from the list.
    setDocumentReviewDataset((current) => reconcileSelectedDataset(current, datasets));
  }, [datasets]);

  const handleDatasetClick = (dataset) => {
    setSelectedDataset(dataset);
  };

  const handleCloseDetails = () => {
    setSelectedDataset(null);
  };

  // Status -> Figma chip variant (PRD-1361): Published is Success, Draft is Standard.
  const getStatusVariant = (status) => (status === 'published' ? 'success' : 'standard');

  const getStatusLabel = (status) => {
    return status === 'published' ? 'Published' : 'Draft';
  };

  // Job status -> Figma chip variant (PRD-1361). Unknown statuses are Standard.
  const getJobStatusVariant = (jobStatus) => {
    const status = (jobStatus || '').toLowerCase();
    if (status === 'complete' || status === 'completed') return 'success';
    if (status === 'awaiting_input') return 'alert';
    if (status === 'processing' || status === 'queued') return 'processing';
    if (status === 'failed' || status === 'error') return 'error';
    return 'standard';
  };

  // Shared with the details modal so the pill label can't drift (PRD-1119).
  // (Previously this mapped failed -> 'Published'; now failed -> 'Failed'.)
  const getJobStatusLabel = (jobStatus) => jobStatusToLabel(jobStatus);

  /** Single status badge: show job state when in progress, otherwise Published/Draft.
   * A just-created dataset has status='published' but no ExtractionJob yet — surface it
   * as "Processing" only when files are actually present and waiting on a job to spawn.
   * An empty dataset (e.g. an automation ran but the source folder had no eligible
   * files) should show its raw status — otherwise the badge stays "Processing" forever
   * since no ExtractionJob will ever materialize. */
  // PRD-979: only during the client-side upload phase — before any
  // ExtractionJob exists — do we want "Uploading files…". Once job_status
  // appears (uploads done, pipeline running) the normal Processing label
  // and accurate count-line take over.
  const isUploadingPhase = (dataset) =>
    autoPublishEnabled && dataset.status === 'draft' && !dataset.job_status;

  // PRD-979: rollup says every file finished and none succeeded — surface as
  // Failed regardless of what dataset.status (or job_status) says. Guards
  // historical rows stuck at 'published' before the DB allowed a real failed
  // status, and job_status races where the aggregate lags reality.
  // Independent of autoPublishEnabled — this is a display correction, not part
  // of the create-flow gating.
  const isAllFailed = (dataset) => {
    // Use the lifecycle rollup (file_counts) — the pipeline is the correct source
    // for a file's real outcome; DatasetFile can lag/stay stale (PRD-1119).
    //
    // awaiting_review is a FOURTH bucket (Stream B): a file parked for field review is
    // neither processing nor ready, so without this term an all-parked dataset counted
    // as all-FAILED — and this check runs before the Review required branch, so the
    // card showed a red Failed chip over a dataset that just needs a human.
    const c = dataset.file_counts;
    return !!c && c.total > 0 && c.processing === 0 && c.ready === 0 && (c.awaiting_review ?? 0) === 0;
  };

  const getDisplayStatus = (dataset) => {
    if (isUploadingPhase(dataset)) return 'Uploading files…';
    if (isAllFailed(dataset)) return 'Failed';
    const job = dataset.job_status?.toLowerCase();
    if (job && !['complete', 'completed'].includes(job)) {
      return getJobStatusLabel(dataset.job_status);
    }
    // A job whose files are ALL parked for document field review reports
    // job_status 'complete' by design, so the legacy label would otherwise read
    // "Published" over a dataset that still needs a human. Checked here rather
    // than relying on `dataset_status_badges` being on, so the meaning of
    // "Published" can't change per environment.
    // Guarded on processing too, so this label switches at the same moment as the
    // count line, the Review button and the toast. job_status and file_counts come
    // from different sources and can disagree transiently while polling; without
    // this a card could read "Review required" with no button to act on.
    if ((dataset.file_counts?.awaiting_review ?? 0) > 0
        && (dataset.file_counts?.processing ?? 0) === 0) {
      return 'Review required';
    }
    const fileCount = dataset.file_summary?.total ?? 0;
    if (!job && dataset.status === 'published' && fileCount > 0) {
      return 'Processing';
    }
    return getStatusLabel(dataset.status);
  };

  const getDisplayStatusVariant = (dataset) => {
    if (isUploadingPhase(dataset)) {
      return getJobStatusVariant('processing');
    }
    if (isAllFailed(dataset)) {
      return getJobStatusVariant('failed');
    }
    const job = dataset.job_status?.toLowerCase();
    if (job && !['complete', 'completed'].includes(job)) {
      return getJobStatusVariant(dataset.job_status);
    }
    // Matches the 'Review required' label getDisplayStatus returns for a
    // fully-parked dataset — amber, same as the CSV awaiting_input chip.
    if ((dataset.file_counts?.awaiting_review ?? 0) > 0
        && (dataset.file_counts?.processing ?? 0) === 0) {
      return getJobStatusVariant('awaiting_input');
    }
    const fileCount = dataset.file_summary?.total ?? 0;
    if (!job && dataset.status === 'published' && fileCount > 0) {
      return getJobStatusVariant('processing');
    }
    return getStatusVariant(dataset.status);
  };

  const loadConnections = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getConnections();
      setConnections(response.items || []);
    } catch (err) {
      console.error('Error loading connections:', err);
      setError(err.message || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'connections') {
      loadConnections();
    }
  }, [activeTab, loadConnections]);

  // Refetch when App.js bumps the shared refresh key (e.g. after review submit / publish)
  useEffect(() => {
    if (datasetsRefreshKey === 0) return;
    loadDatasets(true);
  }, [datasetsRefreshKey, loadDatasets]);

  // PRD-1359 — deep link from the review-required warnings in ResAI and the
  // Automation wizards: /data-sources?review=required[&dataset=<id>].
  //
  // `review=required` applies the existing Review required status filter, which
  // is what the Automations copy promises ("Open in Data Sources" → the Datasets
  // page filtered to review-required). `dataset=<id>` additionally opens that
  // dataset, which is how the ResAI chip gets a reviewer to the parked
  // documents. Deliberately reads once per URL change and does not rewrite the
  // URL, so the browser Back button still leaves the page.
  const appliedDeepLinkRef = useRef('');
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('review') !== 'required') return;
    // Re-applying on every poll would fight the user's own filter changes.
    if (appliedDeepLinkRef.current === location.search) return;
    appliedDeepLinkRef.current = location.search;
    setStatusFilter('review');
    setCurrentPage(1);
  }, [location.search]);

  // Opening the named dataset has to wait for the list, since the card is
  // rendered from a dataset record and the link carries only an id.
  const openedDeepLinkDatasetRef = useRef('');
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wantedId = params.get('dataset');
    if (!wantedId || datasets.length === 0) return;
    if (openedDeepLinkDatasetRef.current === wantedId) return;
    const match = datasets.find((d) => String(d.id) === String(wantedId));
    // Not on this page (filtered out, or on a later page) — leave the filtered
    // list showing rather than guessing.
    if (!match) return;
    openedDeepLinkDatasetRef.current = wantedId;
    setSelectedDataset(match);
  }, [location.search, datasets]);

  // Not flag-gated on purpose — see the flag declaration above. Reaching this
  // handler already requires file_counts.awaiting_review > 0.
  const handleOpenDocumentReview = useCallback((dataset) => {
    setDocumentReviewDataset(dataset);
    documentReviewVm.openReview(dataset.id, dataset.name);
  }, [documentReviewVm]);

  /**
   * The single way out of document review, and therefore the single place that
   * can protect staged work.
   *
   * Six gestures dismiss the panel — Escape, the backdrop, the ✕, the footer's
   * Cancel, the breadcrumb, and every dataset menu action — and all of them end
   * here, in `reset()`, which drops staged resolutions silently. Raised in code
   * review: settle six of eight fields, open the kebab, change your mind, and
   * the work was gone with no warning.
   *
   * Only asks when there is something to lose, so the common case (close a
   * review you have not touched) is still one gesture. Returns whether the
   * close actually happened — callers that do something afterwards, like
   * opening the Edit drawer, must not act when the reviewer said no.
   */
  const handleCloseDocumentReview = useCallback(async () => {
    if (documentReviewVm.hasStagedWork()) {
      const ok = await confirm({
        title: 'Discard your review?',
        message:
          'You have confirmed, corrected or skipped fields on this document that '
          + "haven't been saved yet.\n\n"
          + 'Closing review now discards those decisions. The document stays parked '
          + 'and you can start it again later.',
        confirmLabel: 'Discard',
        variant: 'danger',
      });
      if (!ok) return false;
    }
    setDocumentReviewDataset(null);
    documentReviewVm.reset();
    // Give the backend a moment to transition released documents before the
    // list refetches, matching the CSV review flow's 3s delay.
    setTimeout(() => loadDatasets(true), 3000);
    return true;
  }, [documentReviewVm, loadDatasets, confirm]);

  const handleSaveConnection = async (connectionData) => {
    try {
      await apiService.createConnection(connectionData);
      if (onShowNotification) {
        onShowNotification('Connection created successfully', 'success');
      }
      setShowConnectionModal(false);
      loadConnections();
    } catch (err) {
      console.error('Error creating connection:', err);
      if (onShowNotification) {
        onShowNotification(err.message || 'Failed to create connection', 'error');
      }
    }
  };

  // Close three-dot menu on outside click
  const menuRef = useRef(null);
  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  // Dependent automations unlink via FK ON DELETE SET NULL — they stop until
  // pointed at a new connection, but rows/run-history aren't lost.
  const handleDeleteConnection = async (connection) => {
    const ok = await confirm({
      title: 'Delete connection',
      message:
        `Delete connection "${connection.display_name}"?\n\n` +
        `Any automations that use this connection will be unlinked and stop running until they're pointed at a new connection.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await apiService.deleteConnection(connection.id);
      loadConnections();
    } catch (e) {
      console.error('Failed to delete connection:', e);
      setError(e.message || 'Failed to delete connection');
    }
  };

  const toggleCardMenu = (datasetId) => {
    setOpenMenuId(prev => prev === datasetId ? null : datasetId);
  };

  const handleEditDataset = (dataset) => {
    setOpenMenuId(null);
    setEditingDataset(dataset);
  };

  const handleDeleteDataset = (dataset) => {
    setOpenMenuId(null);
    setDeletingDataset(dataset);
  };

  const handleSaveEdit = async (datasetId, updateData) => {
    await apiService.updateDataset(datasetId, updateData);
    loadDatasets();
  };

  const handleConfirmDelete = async () => {
    // The user already confirmed via DeleteDatasetModal — don't double-prompt.
    // First attempt is safe (no force). If the backend reports active jobs
    // (409), silently retry with force=true; the modal already conveyed the
    // destructive intent and the preview surfaces in-flight work counts.
    try {
      await apiService.deleteDataset(deletingDataset.id);
    } catch (err) {
      if (err?.status === 409) {
        try {
          await apiService.deleteDataset(deletingDataset.id, { force: true });
        } catch (forceErr) {
          if (onShowNotification) {
            onShowNotification(forceErr.message || 'Failed to delete dataset', 'error');
          }
          return;
        }
      } else {
        if (onShowNotification) {
          onShowNotification(err.message || 'Failed to delete dataset', 'error');
        }
        return;
      }
    }
    if (onShowNotification) {
      onShowNotification(`"${deletingDataset.name}" has been deleted`, 'success');
    }
    setDeletingDataset(null);
    loadDatasets();
  };

  const handleTogglePause = async (dataset) => {
    setOpenMenuId(null);
    const wasPaused = Boolean(dataset.paused_at);
    if (!wasPaused) {
      const ok = await confirm({
        title: 'Pause ingestion',
        message:
          `Pause "${dataset.name}"?\n\n` +
          `• Ingestion stops where it is — files mid-pipeline finish their current step but won't advance.\n` +
          `• The dataset is not queryable until you resume it and processing completes.`,
        confirmLabel: 'Pause',
      });
      if (!ok) return;
    }
    try {
      if (wasPaused) {
        await apiService.resumeDataset(dataset.id);
        if (onShowNotification) onShowNotification(`"${dataset.name}" resumed`, 'success');
      } else {
        await apiService.pauseDataset(dataset.id);
        if (onShowNotification) onShowNotification(`"${dataset.name}" paused`, 'success');
      }
      loadDatasets(true);
    } catch (err) {
      if (onShowNotification) {
        onShowNotification(err.message || `Failed to ${wasPaused ? 'resume' : 'pause'} dataset`, 'error');
      }
    }
  };

  // Org-dataset-sharing: owner flips a dataset between private and org-wide (read-only
  // for others). Owner-only + create_edit_dataset — enforced again server-side.
  const handleToggleShare = async (dataset) => {
    setOpenMenuId(null);
    const share = dataset.visibility !== 'organization';
    if (share) {
      const ok = await confirm({
        title: 'Share with organization',
        message:
          `Share "${dataset.name}" with your organization?\n\n` +
          `• Everyone in your org can view it and query it in ResAI.\n` +
          `• Only you can edit, delete, or add files — sharing is read-only for others.`,
        confirmLabel: 'Share',
      });
      if (!ok) return;
    } else {
      // Un-sharing had no confirmation at all, while being the more surprising of
      // the two: it reads like a settings change and silently breaks every widget a
      // colleague built on this dataset, with no notice to them. Your own widgets
      // keep working — you own the dataset — so only other people's are counted.
      let others = null;
      try {
        const impact = await apiService.datasetSharingImpact(dataset.id);
        others = impact?.widget_count_others ?? 0;
      } catch {
        // Degrade to confirming without a number rather than blocking the action or
        // pretending nothing depends on it.
        others = null;
      }
      const ok = await confirm({
        title: 'Make private',
        message:
          `Make "${dataset.name}" private?\n\n` +
          `• Others in your org will no longer be able to view or query it.\n` +
          `${shareImpactMessage(others)}`,
        confirmLabel: 'Make private',
      });
      if (!ok) return;
    }
    try {
      await apiService.updateDataset(dataset.id, { visibility: share ? 'organization' : 'private' });
      if (onShowNotification) {
        onShowNotification(`"${dataset.name}" is now ${share ? 'shared with your organization' : 'private'}`, 'success');
      }
      loadDatasets(true);
    } catch (err) {
      if (onShowNotification) onShowNotification(err.message || 'Failed to update sharing', 'error');
    }
  };


  return (
    <>
      <div className="data-sources-page">
      {/* Assessments keeps its button on the tab row, so it has no header row. */}
      {activeTab !== 'assessments' && (
      <div className="data-sources-header">
        {activeTab === 'datasets' && (
          <button className="data-sources-create-button" onClick={onNavigateToCreate}>
            New Dataset
          </button>
        )}
        {activeTab === 'connections' && (
          <button
            className="data-sources-new-connection-button"
            onClick={() => setShowConnectionModal(true)}
          >
            New Connection
          </button>
        )}
      </div>
      )}

      {/* Tabs */}
      <div className="data-sources-tabs">
        {dataSourcesTabs.map((tab) => (
          <button
            key={tab.id}
            className={`data-sources-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        {/* PRD-1703: on the tab row, right-aligned, as the Navy handoff's section head. */}
        {activeTab === 'assessments' && (
          <button ref={addAssessmentRef} type="button" className="data-sources-create-button assessments-add" onClick={(e) => setAssessmentOpener(e.currentTarget)}>
            Add assessment
          </button>
        )}
      </div>

      {activeTab === 'assessments' && assessmentsModel && (
        <Assessments
          model={assessmentsModel}
          addOpener={assessmentOpener}
          onOpenAdd={setAssessmentOpener}
          onCloseAdd={closeAddAssessment}
          headerAddRef={addAssessmentRef}
          onShowToast={onShowToast}
          onManageScenarios={(opts) => { closeAddAssessment(); onManageScenarios?.(opts); }}
        />
      )}

      {activeTab === 'connections' && (
        <div className="data-sources-description">
          <p>
            Source connections are used to ingest data from cloud storage (Azure Blob, S3, etc.) into datasets via scheduled automations.
          </p>
        </div>
      )}

      {activeTab === 'datasets' && (
        <>
          {/* Status Filter */}
          <div className="data-sources-filters">
            <div className="data-sources-filter-group">
              <span className="data-sources-filter-label">Filter by status:</span>
              <select
                className="resui-select resui-select--compact"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                aria-label="Filter by status"
              >
                <option value="all">All</option>
                <option value="published">Published</option>
                <option value="processing">Processing</option>
                {/* PRD-1359: also shown for the document-review rollout, and
                    unconditionally once the filter is actually applied — a
                    deep link must never land on a select with no matching
                    option, which renders as an empty control. */}
                {(enableCsvHitlReview || documentHitlReviewEnabled || statusFilter === 'review')
                  && <option value="review">Review required</option>}
                <option value="failed">Failed</option>
              </select>
            </div>
            {!loading && !error && (
              <div className="data-sources-results-count">
                Showing {Math.min((currentPage - 1) * pageSize + datasets.length, totalItems)} of {totalItems} datasets
              </div>
            )}
          </div>

          {loading && (
            <div className="data-sources-loading">
              <p>Loading datasets...</p>
            </div>
          )}

          {error && (
            <div className="data-sources-error">
              <p>Error: {error}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="resui-card-grid data-sources-grid">
              {datasets.map((dataset) => {
                const isAwaitingInput = dataset.pipeline_summary?.awaiting_input > 0;
                // Review fields is offered off `isAwaitingReviewOnly`, which reads the
                // COUNT bucket rather than the badge (precedence is
                // processing > failed > review, so a dataset that is both parked and
                // failed carries `action_needed` and a badge test would miss it), and
                // waits for the BATCH to settle — while any file still processes, more
                // documents may yet park, and offering review early hands the reviewer a
                // moving, partial list. Parked docs are never lost by waiting; they hold
                // in AWAITING_REVIEW until reviewed. See utils/datasetReviewState.js.
                // PRD-962: badge === null means an empty dataset (0 files) — show the
                // onboarding prompt instead of a badge. On these roomy cards the count
                // line renders inline under the chip (the dense sidebar uses a tooltip).
                const statusBadge = statusBadgesEnabled ? getDatasetBadge(dataset) : null;
                const statusCountLine = statusBadgesEnabled ? getDatasetCountLine(dataset) : null;
                const showOnboardingPrompt = statusBadgesEnabled && !statusBadge;
                return (
                  <div
                    key={dataset.id}
                    className="resui-card data-sources-card"
                    onClick={() => handleDatasetClick(dataset)}
                  >
                    {/* Actions are owner-only: an org-shared dataset owned by someone
                        else (is_owner === false) is read-only — no menu. */}
                    {/* Title and the three-dot menu share one flex row (PRD-1076 #16).
                        Title flexes (min-width:0 lets it truncate); the menu is a
                        fixed-width sibling instead of an absolutely-positioned overlay. */}
                    <div className="data-sources-card-header">
                      <h3
                        className="data-sources-card-title"
                        data-name={dataset.name}
                        onMouseEnter={(e) => {
                          // Only enable the tooltip when the name is actually clipped
                          const text = e.currentTarget.querySelector('.data-sources-card-title-text');
                          e.currentTarget.dataset.truncated =
                            text && text.scrollWidth > text.clientWidth ? 'true' : 'false';
                        }}
                      >
                        <span className="data-sources-card-title-text">{dataset.name}</span>
                      </h3>
                      {canShowDatasetMenu(dataset, features) && (
                        <div className="data-sources-card-actions" ref={openMenuId === dataset.id ? menuRef : null}>
                          <button
                            type="button"
                            className="data-sources-card-menu-button"
                            aria-label="Dataset actions"
                            onClick={(e) => { e.stopPropagation(); toggleCardMenu(dataset.id); }}
                          >
                            <Icon name="more_vert" />
                          </button>
                          {openMenuId === dataset.id && (
                            <div className="data-sources-card-menu-dropdown">
                              {/* PRD-1359: one shared action list, so the card
                                  menu and the Dataset details menu cannot drift
                                  apart — the feedback doc requires them to
                                  match. */}
                              <DatasetMenuItems
                                dataset={dataset}
                                features={features}
                                close={() => setOpenMenuId(null)}
                                onReviewFields={handleOpenDocumentReview}
                                onToggleShare={handleToggleShare}
                                onEdit={handleEditDataset}
                                onTogglePause={handleTogglePause}
                                onDelete={handleDeleteDataset}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="data-sources-card-details">
                      <span className="data-sources-card-owner">Owner: {dataset.created_by_name || 'N/A'}</span>
                      {/* All badges (Shared / Paused / status) on a single line */}
                      <div
                        className="data-sources-card-badges"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
                      >
                        {/* Published/status badge appears before the Shared badge */}
                        {statusBadgesEnabled
                          ? (!showOnboardingPrompt && (
                              <Chip variant={statusBadge.variant} className="data-sources-badge">
                                {statusBadge.label}
                              </Chip>
                            ))
                          : (
                            // PRD-979: no pill while uploading — the onboarding-style text
                            // renders below, matching the flag-ON path exactly.
                            !isUploadingPhase(dataset) && (
                              <Chip variant={getDisplayStatusVariant(dataset)} className="data-sources-badge">
                                {getDisplayStatus(dataset)}
                              </Chip>
                            )
                          )}
                        {dataset.visibility === 'organization' && (
                          <Chip
                            className="data-sources-badge"
                            title={dataset.is_owner === false
                              ? 'Shared with your organization by its owner (read-only).'
                              : 'Shared with everyone in your organization (read-only for others).'}
                          >
                            {dataset.is_owner === false ? 'Shared with you' : 'Shared'}
                          </Chip>
                        )}
                        {dataset.paused_at && (
                          <Chip
                            className="data-sources-badge"
                            title="New files will not be dispatched until you resume."
                          >
                            Paused
                          </Chip>
                        )}
                      </div>
                      {/* Count line (or onboarding prompt) on its own line below the badges */}
                      {statusBadgesEnabled ? (
                        // PRD-979: while the row is 'draft', suppress the badge + numeric
                        // count line even after file rows start landing — otherwise the
                        // count climbs mid-upload (0 of 25 → 0 of 50) and reads as
                        // misleading progress. Once the row transitions off draft, resume
                        // the normal badge/count-line rendering.
                        showOnboardingPrompt || (dataset.status === 'draft' && !dataset.job_status) ? (
                          <span className="data-sources-onboarding-prompt">
                            {dataset.status === 'draft'
                              ? 'Uploading files…'
                              : 'No files yet — add files to get started'}
                          </span>
                        ) : (
                          statusCountLine && (
                            <span className="data-sources-pipeline-summary">{statusCountLine}</span>
                          )
                        )
                      ) : isUploadingPhase(dataset) ? (
                        <span className="data-sources-onboarding-prompt">Uploading files…</span>
                      ) : dataset.file_counts && dataset.file_counts.total > 0 ? (
                        // PRD-1119: the pipeline lifecycle rollup (file_counts) is the
                        // correct source for a file's real outcome (DatasetFile can lag,
                        // e.g. showing 'complete' for a file that actually failed). Use it
                        // regardless of the auto-publish flag; file_summary is only a
                        // fallback for datasets with no lifecycle row.
                        <span className="data-sources-pipeline-summary">
                          {dataset.file_counts.ready}/{dataset.file_counts.total} files complete
                          {/* needs_attention is the "need attention" bucket (review/triage + failed),
                              not a pure failed count — word it accordingly (matches getDatasetCountLine). */}
                          {dataset.file_counts.needs_attention > 0 && ` · ${dataset.file_counts.needs_attention} need attention`}
                          {/* Document field review sits in its own bucket and is NOT inside
                              needs_attention, so it needs its own term or a fully-parked
                              dataset reads as if nothing needs a human. Settled wording
                              matches the CSV pipeline_summary line below (` · N review`).
                              Design Q3: mid-batch the queue is still named — as progress
                              ("queued for review"), never as an invitation, since the Review
                              button stays withheld until the batch settles. A silent
                              Processing card over several parked files reads as breakage. */}
                          {(dataset.file_counts.awaiting_review ?? 0) > 0
                            && ((dataset.file_counts.processing ?? 0) === 0
                              ? ` · ${dataset.file_counts.awaiting_review} review`
                              : ` · ${dataset.file_counts.awaiting_review} queued for review`)}
                          {dataset.file_counts.processing > 0 && ` · ${dataset.file_counts.processing} processing`}
                        </span>
                      ) : dataset.file_summary && dataset.file_summary.total > 0 ? (
                        <span className="data-sources-pipeline-summary">
                          {dataset.file_summary.complete}/{dataset.file_summary.total} files complete
                          {dataset.file_summary.failed > 0 && ` · ${dataset.file_summary.failed} failed`}
                          {dataset.file_summary.rejected > 0 && ` · ${dataset.file_summary.rejected} rejected`}
                          {dataset.file_summary.processing > 0 && ` · ${dataset.file_summary.processing} processing`}
                        </span>
                      ) : dataset.pipeline_summary && dataset.pipeline_summary.total > 0 && (
                        <span className="data-sources-pipeline-summary">
                          {dataset.pipeline_summary.complete}/{dataset.pipeline_summary.total} files complete
                          {dataset.pipeline_summary.failed > 0 && ` · ${dataset.pipeline_summary.failed} failed`}
                          {dataset.pipeline_summary.awaiting_input > 0 && ` · ${dataset.pipeline_summary.awaiting_input} review`}
                        </span>
                      )}
                      <span className="data-sources-card-updated"><DatasetCardTime dataset={dataset} /></span>
                    </div>
                    {enableCsvHitlReview && isAwaitingInput && !submittedReviewJobs.has(dataset.csv_job_id) && (
                      <button
                        className="data-sources-review-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenReview && onOpenReview(dataset);
                        }}
                      >
                        <Icon name="rate_review" />
                        Review
                      </button>
                    )}
                    {enableCsvHitlReview && isAwaitingInput && submittedReviewJobs.has(dataset.csv_job_id) && (
                      <span className="data-sources-review-submitted">
                        <Icon name="check_circle" />
                        Review submitted — processing
                      </span>
                    )}
                    {/* PRD-1359 (feedback doc §Dataset card): the document-review
                        Review button moved into the card context menu as "Review
                        Fields". The CSV review button above is a different flow and
                        stays. */}
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !error && datasets.length === 0 && (
            <div className="data-sources-empty">
              <p>No datasets found. Create your first data source to get started.</p>
            </div>
          )}

          {!loading && !error && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}

        </>
      )}

      {activeTab === 'connections' && (
        <>
          {loading && (
            <div className="data-sources-loading">
              <p>Loading connections...</p>
            </div>
          )}

          {error && (
            <div className="data-sources-error">
              <p>Error: {error}</p>
            </div>
          )}

          {!loading && !error && (
            <div className="data-sources-connections-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Location</th>
                    <th>Root Prefix</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {connections.map((connection) => {
                    const TYPE_LABELS = {
                      azure_blob: 'Azure Blob',
                      aws_s3: 'AWS S3',
                      sharepoint: 'SharePoint',
                      hosted_pdf: 'Hosted PDF',
                    };
                    const typeLabel = TYPE_LABELS[connection.connection_type] || connection.connection_type;
                    return (
                    <tr key={connection.id}>
                      <td>{connection.display_name}</td>
                      <td>
                        <Chip className="data-sources-connection-type">{typeLabel}</Chip>
                      </td>
                      <td>{connection.location}</td>
                      <td>
                        {/* SharePoint stores its folder in config.folder_path; root_prefix
                            is only synced at creation and goes stale on edit (PRD-1119).
                            Show the field ingestion actually uses per connection type. */}
                        {(connection.connection_type === 'sharepoint'
                          ? connection.config?.folder_path
                          : connection.root_prefix) || '/'}
                      </td>
                      <td>
                        <RowActionsMenu ariaLabel="Connection actions">
                          {(close) => (
                            <>
                              <button
                                onClick={() => {
                                  close();
                                  setEditingConnection(connection);
                                }}
                              >
                                <Icon name="edit" /> Edit
                              </button>
                              <button
                                className="row-actions-menu-danger"
                                onClick={() => {
                                  close();
                                  handleDeleteConnection(connection);
                                }}
                              >
                                <Icon name="delete" /> Delete
                              </button>
                            </>
                          )}
                        </RowActionsMenu>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && connections.length === 0 && (
            <div className="data-sources-empty">
              <p>No connections found. Create your first connection to get started.</p>
            </div>
          )}
        </>
      )}
      </div>

      {selectedDataset && (
        <DatasetDetails
          dataset={selectedDataset}
          onClose={handleCloseDetails}
          enableCsvHitlReview={enableCsvHitlReview}
          reviewSubmitted={submittedReviewJobs.has(selectedDataset?.csv_job_id)}
          // PRD-1359: the details overflow menu renders the same list as the
          // card menu, so it needs the same handlers.
          onToggleShare={handleToggleShare}
          onTogglePause={handleTogglePause}
          onOpenDocumentReview={(ds) => {
            // Close the details modal first, exactly as the CSV entry does —
            // otherwise the review modal stacks on top of another modal.
            handleCloseDetails();
            handleOpenDocumentReview(ds);
          }}
          onOpenReview={(dataset) => {
            handleCloseDetails();
            onOpenReview && onOpenReview(dataset);
          }}
          features={features}
          showAiIndexedSummary={showAiIndexedSummary}
          onEdit={handleEditDataset}
          onDelete={handleDeleteDataset}
        />
      )}

      {showConnectionModal && (
        <AddConnectionModal
          isOpen={showConnectionModal}
          onClose={() => setShowConnectionModal(false)}
          onSave={handleSaveConnection}
          onShowNotification={onShowNotification}
        />
      )}


      {/* Rendered at component top level, NOT inside the datasets tab: a
          fixed-position modal must not be unmounted by a background tab switch,
          which would drop the reviewer's staged work mid-review. CsvReviewModal
          goes further and mounts in App.js outside <Routes>; matching that fully
          would also let this open from Ask ResAI. */}
      {documentReviewDataset && (
        <DocumentReviewPanel
          vm={documentReviewVm}
          onClose={handleCloseDocumentReview}
          onShowNotification={onShowNotification}
          // PRD-1359 (Figma 1018-2835): back returns to Dataset details rather
          // than just closing, which is what the breadcrumb promises. Review is
          // reachable from the card menu too, where no details modal was ever
          // open — so this OPENS details rather than restoring a previous one.
          // Always available: the record is the one the opener handed us, so
          // paginating or filtering the list behind the modal cannot take the
          // way back away.
          onBack={async () => {
            // Awaited: if the reviewer backs out of the discard confirm, review
            // must stay open rather than being replaced by Dataset details.
            if (await handleCloseDocumentReview()) {
              setSelectedDataset(documentReviewDataset);
            }
          }}
          // PRD-1359: the dataset context menu the feedback doc asks for on the
          // Review fields and Document View headers. Built here rather than in
          // the review components, which have no business knowing about share,
          // pause or delete.
          //
          // "Review fields" is deliberately not offered — the reviewer is
          // already in it. Every other action closes review first: they each
          // open a drawer or a confirm of their own, and none may layer.
          //
          // NOTE: Delete is reachable from here because the doc asks the menu to
          // be the dataset's. It discards staged review work, exactly as it
          // already does from the card. If that lands badly in review, the fix
          // is a confirm that names the cost, not dropping the item.
          // Gated as the card and the details modal are — the review header
          // offered the same owner-only actions with no ownership or
          // capability check at all.
          menu={canShowDatasetMenu(documentReviewDataset, features) && (
            <RowActionsMenu ariaLabel="Dataset actions" menuClassName="dataset-details-actions-menu">
              {(close) => (
                <DatasetMenuItems
                  dataset={documentReviewDataset}
                  features={features}
                  close={close}
                  // Each awaits the close: the confirm can be declined, and
                  // firing the action anyway would both keep the staged work and
                  // act on the dataset behind a panel the reviewer chose to keep.
                  onToggleShare={async (ds) => { if (await handleCloseDocumentReview()) handleToggleShare(ds); }}
                  onEdit={async (ds) => { if (await handleCloseDocumentReview()) handleEditDataset(ds); }}
                  onTogglePause={async (ds) => { if (await handleCloseDocumentReview()) handleTogglePause(ds); }}
                  onDelete={async (ds) => { if (await handleCloseDocumentReview()) handleDeleteDataset(ds); }}
                  deleteClassName="dataset-details-menu-delete"
                />
              )}
            </RowActionsMenu>
          )}
        />
      )}

      {confirmDialog}

      {deletingDataset && (
        <DeleteDatasetModal
          dataset={deletingDataset}
          onClose={() => setDeletingDataset(null)}
          onConfirm={handleConfirmDelete}
          onShowNotification={onShowNotification}
        />
      )}

      {editingDataset && (
        <EditDatasetModal
          dataset={editingDataset}
          onClose={() => {
            setEditingDataset(null);
            loadDatasets(true);
          }}
          onSave={handleSaveEdit}
          onShowNotification={onShowNotification}
          // PRD-1359: "Review fields" link beside the review chip. Closes the
          // drawer first — the feedback doc is explicit that review must not be
          // layered on top of another modal.
          onOpenDocumentReview={(ds) => {
            setEditingDataset(null);
            handleOpenDocumentReview(ds);
          }}
        />
      )}

      {editingConnection && (
        <AddConnectionModal
          isOpen={true}
          onClose={() => {
            setEditingConnection(null);
            loadConnections();
          }}
          onSave={() => {
            setEditingConnection(null);
            loadConnections();
          }}
          onShowNotification={onShowNotification}
          editingConnection={editingConnection}
        />
      )}
    </>
  );
};

export default DataSources;
